import { RawWidgetsLayout, WidgetsLayout } from '@types';
import { generateFallbackLayout } from './sources/fallback';
import { setCachedLayout, getLayoutFromCache } from './sources/cache';
import { loadAndShowError } from './error-loader';
import { failedToLoadLayoutFromCache, failedToLoadLayoutFromDealer } from '@services/sentry';
import { ENABLE_DASHBOARD_CUSTOMIZATION_SPEC, fedopsPhases } from '@consts';
import type { FlowAPILocalInstances, FlowAPIInstances, FlowAPIBase } from 'yoshi-flow-bm-runtime';
import { getDashboardData } from '../../api';
import { mapLayout } from './helpers/map-layout';

export interface FetchWidgetLayoutParams {
  metaSiteId: string;
  userId: string;
  userPermissions: string[];
  fedopsLogger: FlowAPILocalInstances['fedopsLogger'];
  sentry: FlowAPIBase['sentry'];
  biLogger: FlowAPIInstances['biLogger'];
  experiments: FlowAPIInstances['experiments'];
  httpClient: FlowAPIBase['httpClient'];
  i18n: FlowAPIInstances['i18n'];
}

export const loadLayout = async ({
  metaSiteId,
  userId,
  userPermissions,
  fedopsLogger,
  sentry,
  biLogger,
  httpClient,
  experiments,
  i18n,
}: FetchWidgetLayoutParams): Promise<WidgetsLayout> => {
  const sentryMetaData = {
    userId,
    metaSiteId,
  };

  try {
    const enableCustomization = experiments.enabled(ENABLE_DASHBOARD_CUSTOMIZATION_SPEC);
    fedopsLogger.appLoadingPhaseStart(fedopsPhases.LOAD_DEALER_LAYOUT);

    const layout: RawWidgetsLayout = (
      await httpClient.request(getDashboardData)({ enableCustomization })
    ).layout;

    fedopsLogger.appLoadingPhaseFinish(fedopsPhases.LOAD_DEALER_LAYOUT);
    setCachedLayout(metaSiteId, layout).catch();

    return mapLayout({ sentry, sentryMetaData, layout, userPermissions });
  } catch (e) {
    try {
      failedToLoadLayoutFromDealer(sentry, e, sentryMetaData);
      const cachedLayout = await getLayoutFromCache(metaSiteId);

      if (cachedLayout) {
        biLogger
          .dashboardFetchDealerLayoutFailed({
            msid: metaSiteId,
            type: 'cached',
            dashboard_type: 'new',
          })
          .catch();

        return mapLayout({ sentry, sentryMetaData, layout: cachedLayout, userPermissions });
      }
    } catch (cacheError) {
      failedToLoadLayoutFromCache(sentry, cacheError, sentryMetaData);
    }
  }

  biLogger
    .dashboardFetchDealerLayoutFailed({
      msid: metaSiteId,
      type: 'fallback',
      dashboard_type: 'new',
    })
    .catch();
  loadAndShowError(i18n);

  return generateFallbackLayout();
};
